import { setTooltip } from '../presentation/tooltips.js';
import '../presentation/styles/index.css';
import { createAccountService, type User } from '../authentication/account-service.js';
import { bindAccountPanel, showAccountError } from '../presentation/accounts/account-panel.js';
import { mountGameShell } from '../presentation/shell/game-shell.js';
import { FullscreenControls } from '../presentation/shell/fullscreen-controls.js';
import { mountGameSettings } from '../presentation/shell/game-settings.js';
import { refreshCombatLog } from '../presentation/settings/shared-tabs.js';
import { DeveloperMenu } from '../presentation/shell/developer-menu.js';
import { MainMenu } from '../presentation/shell/main-menu.js';
import { mountClickParticles } from '../presentation/shell/click-particles.js';
import { installCursors } from '../presentation/cursors.js';
import { keepLobby, leaveLobby, watchSlabs } from '../presentation/shell/slab-motion.js';
import { graphicsOptions } from '../rendering/graphics-options.js';
import { applyGameLook } from '../rendering/picture-filters.js';
import { GameMusic } from '../audio/game-music.js';
import { Ambience } from '../audio/ambience.js';
import { ThemeMusic } from '../presentation/shell/theme-music.js';
import { element as el } from '../presentation/dom.js';
import { gameContent } from '../../content/game-content.js';
import { publicEnemy } from '../../shared/game/public-content.js';
import { readInviteForForm, createInviteUrl, PUBLIC_CLIENT_URL } from '../../shared/network/invites.js';
import { GameConnection } from '../networking/game-connection.js';
import { GameScene } from '../rendering/game-scene.js';
import { ContextRecovery } from '../rendering/context-recovery.js';
import { preparationFrame } from '../rendering/idle-preparation.js';
import { preloadCombatSounds } from '../audio/combat-sounds.js';
import { preloadCritterSounds } from '../audio/critter-sounds.js';
import { autoVoices, heroVoice, prepareVoices, setVoicePlacement, speak, speakCommand } from '../audio/hero-voice.js';
import { WorldLoading } from '../presentation/shell/world-loading.js';
import { formatClock } from '../../shared/game/rest-types.js';
import { PartyPicker } from '../presentation/party/party-picker.js';
import { CharacterCreator } from '../presentation/characters/character-creator.js';
import { CharacterStore } from '../characters/character-store.js';
import { seedStarterCharacters } from '../characters/starter-characters.js';
import { clientConfig } from '../configuration/client-config.js';
import type { GameContent } from '../../shared/game/world-types.js';
import type { WorldSnapshot } from '../../shared/network/protocol.js';
import { WorldHud } from '../presentation/hud/world-hud.js';
import { WorldHud3d } from './world-hud-3d.js';
import { markPerformance } from '../performance-mark.js';
import { setPageTitle } from '../page-title.js';

mountGameShell();
/** Built before anything binds the buttons and readouts its tabs hold. */
const settingsMenu = mountGameSettings(el('app'), {
  signedIn: () => !!user, inRoom: () => inWorld, developer: () => !!developerMenu, releaseCursor: () => fullscreenControls.releaseCursor(),
});
const worldLoading = new WorldLoading();
let worldGeneration = 0, worldPreparing = false, ownIds: readonly string[] = [];
/** Where the party will stand, for the loading screen's map: the middle of these heroes' spawns, or of every hero's. */
function partyStand(content: typeof gameContent, ids: readonly string[]) {
  const heroes = content.heroes.filter(hero => ids.includes(hero.id)), spots = (heroes.length ? heroes : content.heroes).map(hero => hero.spawn);
  return spots.length ? { x: spots.reduce((sum, spot) => sum + spot.x, 0) / spots.length, z: spots.reduce((sum, spot) => sum + spot.z, 0) / spots.length } : { x: 0, z: 0 };
}
function showMap(content: typeof gameContent) {
  setPageTitle(`NimbleZ · ${content.map.name}`);
}
showMap(gameContent);
watchSlabs(); mountClickParticles(); void installCursors();
new Ambience();
const gameMusic = new GameMusic();
// The theme waits for the gem: only the countryside bed plays behind the sealed door.
const themeMusic = new ThemeMusic();
let splashOpened = false;
const mainMenu = new MainMenu(() => { themeMusic.play(); splashOpened = true; scene?.beginSplash(); }, () => settingsMenu.show(), () => openCreator());
worldLoading.mirror(message => mainMenu.setStatus(message));
const characters = new CharacterStore();
/** Where the creator returns: the party picker when it was opened from there, otherwise the main menu. */
let creatorReturn: 'menu' | 'play' = 'menu', creatorAfterSignIn = false;
const creator = new CharacterCreator(el('creator'), characters, {
  done: saved => { if (saved) notice(`${saved.name} is ready.`); mainMenu.show(creatorReturn); },
});
function openCreator(existing?: import('../../shared/game/character-build.js').CharacterBuild) {
  // Characters belong to an account: a signed-out player is sent to sign in, and the creator opens when they have.
  if (!user) { creatorAfterSignIn = true; mainMenu.show('play'); return; }
  creatorReturn = mainMenu.view === 'play' ? 'play' : 'menu'; el('lobby-error').textContent = '';
  mainMenu.show('create'); creator.open(existing);
}
/** True from entering a world until the player is back at the lobby, which then opens on the play page rather than the main menu. */
let inWorld = false;
let user: User | null = null, snapshot: WorldSnapshot | null = null, connected = false;
let content: GameContent = gameContent;
let toastTimer: ReturnType<typeof setTimeout>;
function notice(message: string) {
  el('toast').textContent = message; el('toast').hidden = false;
  clearTimeout(toastTimer); toastTimer = setTimeout(() => { el('toast').hidden = true; }, 5000);
}
const hud = new WorldHud({
  send: message => connection.send(message),
  connected: () => connected,
  notice,
  setSelection: ids => scene?.setSelection(ids),
  speak: (heroIds, event, state, owned) => speak(heroIds, event, state, owned),
  command: (message, state, owned) => speakCommand(message, state, owned),
  modalOpen: () => settingsMenu.open,
  // The clock, the room's player count and the menus live in the game's shell, not the HUD.
  refreshed: state => {
    const clock = formatClock(state.clock);
    el('world-clock').hidden = false; el('world-clock-value').textContent = clock;
    setTooltip(el('world-clock'), `${clock}. In-game time advances as you play and jumps when the party rests.`, `In-game time: ${clock}`);
    const players = new Set(state.heroes.filter(h => h.connected).map(h => h.playerSlot)).size;
    el('players').textContent = `${players} player${players === 1 ? '' : 's'} · ${state.heroes.length} / ${content.heroes.length} heroes`;
    el<HTMLButtonElement>('restart-encounter').disabled = false;
    refreshCombatLog(); developerMenu?.update(state);
  },
}, gameContent);
const fullscreenControls = new FullscreenControls(el('app'), el<HTMLButtonElement>('fullscreen'),
  el<HTMLButtonElement>('capture-cursor'), () => !!(hud.selection.owned.length > 0 && el('lobby').hidden), notice);
let developerMenu: DeveloperMenu | null = null;
let devSplashClock: number | null = null;
// Close the modal before requesting cursor capture from these user gestures.
for (const id of ['fullscreen', 'capture-cursor']) el(id).addEventListener('click', () => settingsMenu.close(), { capture: true });
let invite = { serverUrl: null, room: 'CROSSING', sessionKey: null } as ReturnType<typeof readInviteForForm>;
let validInvite = true;
try { invite = readInviteForForm(location.href); } catch (error) { validInvite = false; showAccountError(error); }
const waitingForInvite = !invite.serverUrl && location.hostname === new URL(PUBLIC_CLIENT_URL).hostname;
if (waitingForInvite) {
  validInvite = false;
  el<HTMLButtonElement>('room-form').querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled = true;
  el('room-hint').textContent = 'Start the host utility on one player’s PC, then open its invite link.';
}
el('host-address').textContent = invite.serverUrl ? `Player host: ${new URL(invite.serverUrl).hostname}` : waitingForInvite ? 'Open an invite from your party’s host to join.' : 'Connect to the local game server.';
const connection = new GameConnection(async () => {
  if (!user) throw new Error('Sign in first.');
  return user.getIdToken(true);
}, {
  status: (message, isConnected) => {
    connected = isConnected; el('network-status').lastChild!.textContent = message;
    el('network-status').classList.toggle('online', isConnected);
    if (message === 'Disconnected') { worldGeneration++; worldPreparing = false; worldLoading.hide(); partyPicker.setBusy(false); el<HTMLButtonElement>('enter-game').disabled = true; el<HTMLButtonElement>('choose-party').disabled = !validInvite; }
  },
  party: options => {
    worldGeneration++; worldPreparing = false; worldLoading.hide();
    hud.reset(); snapshot = null; scene?.clearWorld(); gameMusic.stop();
    for (const id of ['hud', 'leave', 'combat-status', 'paused-banner', 'world-clock', 'combat-log']) el(id).hidden = true;
    inWorld = false; mainMenu.show('play'); keepLobby();
    el('lobby').hidden = false; el('room-panel').hidden = true; el('party-panel').hidden = false;
    el('lobby-title').textContent = 'Choose your party'; el('lobby-error').textContent = '';
    partyPicker.show(options); fullscreenControls.refresh();
  },
  welcome: async (ids, room, nextContent) => {
    const generation = ++worldGeneration;
    markPerformance('nimblez:welcome');
    worldPreparing = true; ownIds = ids; worldLoading.chart(nextContent.map, partyStand(nextContent, ids)); worldLoading.show('Preparing the world…');
    content = nextContent; void preloadCombatSounds([...content.enemies, ...Object.values(content.enemySummons ?? {})]); prepareVoices(content.heroes); heroVoice.reset(); autoVoices.reset(); gameMusic.stop(); developerMenu?.reset(); hud.enter(ids, content); snapshot = null;
    // Entering before the lobby's backdrop is ready waits for it too: the first half of the bar is the lobby's own progress.
    const lobbyShare = lobbyProgress < 1 ? .5 : 0;
    const entering = (message: string, progress: number) => { if (generation === worldGeneration) worldLoading.show(message, progress); };
    try {
      // Let the overlay paint before anything heavy runs.
      await preparationFrame();
      if (generation !== worldGeneration) return;
      // The lobby's warm scene takes the party in place; only an unfamiliar map or a failed lobby builds a new one.
      lobbyWatcher = lobbyShare ? (message, progress) => entering(message, lobbyShare * progress) : null;
      await lobbyReady;
      lobbyWatcher = null;
      if (generation !== worldGeneration) return;
      if (!scene?.adopt(content)) { scene?.dispose(); scene = createScene(content); scene.previewClock = devSplashClock; }
      await scene.prepare(false, (message, progress) => entering(message, lobbyShare + (1 - lobbyShare) * progress));
    } catch {
      if (generation !== worldGeneration) return;
      scene?.dispose(); scene = null; leave();
      el('lobby-error').textContent = 'Could not prepare the world. Check your connection and WebGL support, then try entering again.';
      return;
    }
    if (generation !== worldGeneration) return;
    worldPreparing = false;
    scene.setOwnHeroes(ids); showMap(content);
    hud.heroesChanged();
    el('room-label').textContent = room; leaveLobby(); themeMusic.stop(); inWorld = true;
    for (const id of ['hud', 'leave', 'combat-status']) el(id).hidden = false;
    fullscreenControls.refresh();
    history.replaceState(null, '', createInviteUrl(location.href, { ...invite, room }));
    showLatestSnapshot();
    worldLoading.hide();
    markPerformance('nimblez:world-ready');
  },
  heroes: heroes => {
    // Another player filled their seats: draw the heroes they brought.
    for (const hero of heroes) { const index = content.heroes.findIndex(candidate => candidate.id === hero.id); if (index >= 0) content.heroes[index] = hero; scene?.replaceHero(hero); }
    prepareVoices(content.heroes);
    hud.heroesChanged();
  },
  snapshot: state => { snapshot = state; hud.snapshot = state; if (worldPreparing) return; const shown = scene?.receive(state) ?? state; autoVoices.receive(shown, hud.selection.owned); if (inWorld) gameMusic.update(state.inCombat); hud.refresh(); },
  error: message => { partyPicker.setBusy(false); el<HTMLButtonElement>('choose-party').disabled = !validInvite; el('lobby-error').textContent = message; notice(message); },
  latency: ms => { el('latency').textContent = `${ms} ms round trip · 30 ticks/s`; },
  ping: ping => { if (!worldPreparing) scene?.showPing(ping); },
}, invite.serverUrl || location.origin);
const partyPicker = new PartyPicker(characters, {
  enter: choice => { el('lobby-error').textContent = ''; return connection.enter(choice); },
  create: () => openCreator(), edit: build => openCreator(build),
});
let scene: GameScene | null = null;
setVoicePlacement(id => scene?.voicePlacement(id) ?? null);
function showLatestSnapshot() {
  if (snapshot) { scene?.receive(snapshot); gameMusic.update(snapshot.inCombat); hud.receive(snapshot); }
}
// The party column, radial menu and command rail, drawn over the world after each frame; nothing while the lobby is shown.
const hud3d = new WorldHud3d(hud, el('stage'), () => scene);
const contextRecovery = new ContextRecovery({ show: (message, progress) => worldLoading.show(message, progress), hide: () => { if (!worldPreparing) worldLoading.hide(); },
  failed: message => worldLoading.retry(message), reload: () => location.reload() });
function createScene(sceneContent: GameContent) {
  const created = new GameScene(el('stage'), (point, turn) => hud.move(point, turn), sceneContent, id => hud.attack(id), (ids, mode) => hud3d.select(ids, mode), hud.supplyHandlers,
    () => hud.enemyTargetAllowed(), id => hud.allyFigureTargetAllowed(id));
  created.setOverlay(hud3d.draw, hud3d.warm, hud3d.clear);
  created.contextHandlers = { lost: () => contextRecovery.lost(), restored: rebuilt => contextRecovery.restored(rebuilt), progress: (message, fraction) => contextRecovery.progress(message, fraction) };
  // New lighting relinks the lit shaders behind the loading overlay, as a new Character detail rebuilds the figures.
  let relinking = false;
  created.lightingHandlers = {
    progress: (message, fraction) => {
      if (worldPreparing) return;
      if (!relinking) { relinking = true; worldLoading.chart(inWorld ? content.map : null, partyStand(content, ownIds)); }
      worldLoading.show(message, fraction);
    },
    done: () => { if (!relinking) return; relinking = false; if (worldPreparing) return; worldLoading.hide(); if (inWorld) showLatestSnapshot(); },
  };
  return created;
}
// The lobby's 3D backdrop prepares in the background while the gate and menus stay usable; entering a world waits for it.
// Sound is optional and never holds the player back.
let lobbyProgress = 0, lobbyWatcher: ((message: string, progress: number) => void) | null = null;
async function prepareLobby() {
  // The backdrop's critters only; battle sounds wait for a world (`welcome`), leaving the connection to the backdrop's rigs.
  void preloadCritterSounds();
  try {
    const lobbyContent = { ...gameContent, enemies: [] }, enemies = gameContent.enemies.map(publicEnemy);
    scene = createScene(lobbyContent); scene.previewClock = devSplashClock; scene.expectEnemies(enemies);
    await scene.prepare(true, (message, progress) => { lobbyProgress = progress; worldLoading.note(message, progress); lobbyWatcher?.(message, progress); });
    if (splashOpened) scene.beginSplash();
    scene.prewarmEnemies(enemies);
  } catch {
    scene?.dispose(); scene = null;
    el('lobby-error').textContent = 'Could not prepare the 3D map. Check WebGL support and reload.';
  }
  lobbyProgress = 1;
  markPerformance('nimblez:lobby-ready');
  worldLoading.endNote();
  void gameMusic.preload(); // Needed at the first steps in the world and the first fight: it starts only once the lobby is ready, so it never competes with startup resources.
}
const lobbyReady = prepareLobby();
// The picture style (`picture-filters.ts`) follows its setting; scenarios and the explorer never turn it on by themselves.
applyGameLook(graphicsOptions.pictureStyle);
graphicsOptions.onChange(() => applyGameLook(graphicsOptions.pictureStyle));
// A new Character detail rebuilds every figure behind the loading overlay, in the world or the lobby.
let figureDetail = graphicsOptions.characterDetail;
graphicsOptions.onChange(() => {
  if (graphicsOptions.characterDetail === figureDetail) return;
  figureDetail = graphicsOptions.characterDetail;
  void lobbyReady.then(async () => {
    if (!scene || worldPreparing) return;
    const generation = worldGeneration;
    worldLoading.chart(inWorld ? content.map : null, partyStand(content, ownIds)); worldLoading.show('Preparing the world…');
    try { await scene.rebuildFigures(!inWorld, (message, progress) => { if (generation === worldGeneration) worldLoading.show(message, progress); }); }
    finally { if (generation === worldGeneration) { worldLoading.hide(); if (inWorld) showLatestSnapshot(); } }
  });
});
mainMenu.waitFor(lobbyReady);
el('restart-encounter').addEventListener('click', () => connection.send({ type: 'restart' }));
// Developer tools exist only in the Vite dev client; the server also refuses them unless it runs against the auth emulator.
if (import.meta.env.DEV) developerMenu = new DeveloperMenu(message => connection.send(message), settingsMenu, () => snapshot, minute => { devSplashClock = minute; if (scene) scene.previewClock = minute; });
if (import.meta.env.DEV) void import('../rendering/profiling/game-console.js').then(({ createGameConsole }) => {
  // `hud` drives the 3D HUD as clicks would, for browser checks: select heroes, press a rail button, open the menu on a unit, read a medallion.
  Object.assign(window, { game: createGameConsole(() => scene), hud: {
    select: (ids: string[]) => hud3d.select(ids, 'replace'), press: (id: string) => { hud3d.pressDock(id); return hud3d.menu(); },
    menu: (unitId: string | null) => { hud3d.open(unitId); return hud3d.menu(); }, hero: (id: string) => hud3d.heroText(id),
  } });
});
document.addEventListener('keydown', event => { if (!hud3d.key(event)) hud.key(event); });
function leave() {
  worldGeneration++; worldPreparing = false; worldLoading.hide();
  settingsMenu.close();
  fullscreenControls.releaseCursor();
  connection.disconnect(); connected = false; hud.reset(); snapshot = null; scene?.clearWorld(); gameMusic.stop();
  for (const id of ['hud', 'leave', 'paused-banner', 'combat-log', 'combat-status', 'behavior-panel', 'loot-panel', 'rest-panel', 'world-clock']) el(id).hidden = true;
  if (inWorld) { inWorld = false; mainMenu.show('play'); themeMusic.play(); }
  keepLobby(); el('lobby').hidden = false; el('network-status').classList.remove('online'); el('network-status').lastChild!.textContent = 'Not connected';
  el('party-panel').hidden = true; el('room-panel').hidden = !user; el('lobby-title').textContent = user ? 'Join room' : 'Sign in';
  el<HTMLButtonElement>('choose-party').disabled = !validInvite;
  fullscreenControls.refresh();
}
el('leave').addEventListener('click', leave);
el('party-back').addEventListener('click', leave);
el('invite').addEventListener('click', async () => {
  try { await navigator.clipboard.writeText(location.href); notice('Invite link copied. Your friend needs a different account.'); }
  catch { notice('Copy the current browser URL to invite your friend.'); }
});
el<HTMLInputElement>('room-code').value = invite.room;

try {
  const accounts = await createAccountService();
  if (accounts.emulator) { el('mode').textContent = 'LOCAL AUTH EMULATOR · TEST ACCOUNTS ONLY'; el('mode').hidden = false; }
  characters.attach(accounts.app, clientConfig.firestoreEmulatorHost);
  accounts.subscribe(next => {
    user = next; characters.setAccount(next?.uid ?? null);
    if (next && accounts.emulator) void seedStarterCharacters(characters);
    if (next && creatorAfterSignIn && mainMenu.view === 'play') openCreator();
    creatorAfterSignIn = false;
    // Signing out mid-draft drops the draft.
    if (!next && mainMenu.view === 'create') mainMenu.show('menu');
    el('menu-create').querySelector<HTMLElement>('.menu-lock')!.hidden = !!next;
    if (next) delete el('menu-create').dataset.tooltip; else setTooltip(el('menu-create'), 'Sign in to create characters');
    el('auth-panel').hidden = !!next; el('room-panel').hidden = !next;
    el('lobby-title').textContent = next ? 'Join room' : 'Sign in';
    if (next) el('account').textContent = next.displayName || next.email || 'Adventurer'; else leave();
  });
  bindAccountPanel(accounts, leave);
  el('room-form').addEventListener('submit', event => {
    event.preventDefault(); if (!user || !validInvite) return;
    el('lobby-error').textContent = ''; el<HTMLButtonElement>('choose-party').disabled = true;
    connection.connect(el<HTMLInputElement>('room-code').value.trim().toUpperCase(), invite.sessionKey || undefined);
  });
} catch (error) { showAccountError(error); }
